import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { Box, Button, IconButton, ListItemIcon, MenuItem, ThemeProvider, Tooltip, useMediaQuery } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { Context } from '../../../context/UserContext';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import { useMRTState } from '../../../hooks/useMRTState';
import { podeIncluir, podeEditar, podeCancelar, podeImprimir } from '../../../hooks/usePermisionMRT';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import useFlashMessage from '../../../hooks/useFlashMessage';
import { obterPermissoesUsuario, printPdf } from '../../../utils/funcoes';
import CrudsAPI from '../../../api/CRUDs';
import CustomDialog from '../../layout/CustomDialog';
import tableThemeCustom from '../../form/tableThemeCustom';

const Index = () => {
    const [dados, setDados] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [permissoesCarregadas, setPermissoesCarregadas] = useState(false);
    const requestId = useRef(0);
    const carregou = useRef(false);
    const excluindo = useRef(false);
    const { parametro } = useContext(Context);
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();
    const navigateAuth = useNavigate();
    const token = localStorage.getItem('token');
    const empresaId = localStorage.getItem('empresaId');
    const pessoaId = localStorage.getItem('pessoaId');
    const urlParams = new URLSearchParams(window.location.search);
    const ul = urlParams.get('ul') || 2;
    const li = urlParams.get('li') || 76;
    const theme = tableThemeCustom();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const mrt = useMRTState({
        storageKey: 'Cargo.Index',
        pageSizeDefault: Number(parametro?.paginacao) || 5,
    });
    const {
        columnFilters,
        columnFilterFns,
        globalFilter,
        sorting,
        pagination,
        setIsError,
        setIsLoading,
        setIsRefetching,
        setRowCount,
    } = mrt;
    const mrtDefault = getMRTDefaultProps({ mrt, parametro, isMobile });

    useEffect(() => {
        if (!token) {
            navigateAuth('/Auth', { replace: true });
            return;
        }
        let ativo = true;
        setPermissoesCarregadas(false);
        Promise.resolve()
            .then(() => obterPermissoesUsuario(pessoaId, empresaId, ul, li))
            .then(() => {
                if (ativo) setPermissoesCarregadas(true);
            })
            .catch((error) => {
                console.error(error);
                if (ativo) setFlashMessage('Não foi possível carregar as permissões.', 'error');
            });
        return () => {
            ativo = false;
        };
    }, [token, pessoaId, empresaId, ul, li, navigateAuth, setFlashMessage]);

    const fetchData = useCallback(async () => {
        const requisicao = ++requestId.current;
        setIsLoading(!carregou.current);
        setIsRefetching(carregou.current);
        try {
            const { data } = await CrudsAPI.cargo.listar(
                pagination.pageIndex * pagination.pageSize,
                pagination.pageSize,
                JSON.stringify(columnFilters ?? []),
                JSON.stringify(columnFilterFns ?? {}),
                globalFilter ?? '',
                JSON.stringify(sorting ?? [])
            );
            if (requisicao !== requestId.current) return;
            setDados(data.data);
            setRowCount(data.meta.totalRowCount);
            setIsError(false);
            carregou.current = true;
        } catch (error) {
            if (requisicao !== requestId.current) return;
            setIsError(true);
            console.error(error);
        } finally {
            if (requisicao === requestId.current) {
                setIsLoading(false);
                setIsRefetching(false);
            }
        }
    }, [
        pagination.pageIndex,
        pagination.pageSize,
        columnFilters,
        columnFilterFns,
        globalFilter,
        sorting,
        setIsError,
        setIsLoading,
        setIsRefetching,
        setRowCount,
    ]);

    useEffect(() => {
        if (token) fetchData();
        return () => {
            requestId.current += 1;
        };
    }, [token, empresaId, fetchData]);

    const abrirCadastro = (row) => {
        if (!permissoesCarregadas || !(row ? podeEditar() : podeIncluir())) return;
        const params = new URLSearchParams(window.location.search);
        params.set('ul', ul);
        params.set('li', li);
        params.set('btnText', row ? 'Atualizar' : 'Cadastrar');
        if (row) params.set('uuid', row.id);
        else params.delete('uuid');
        navigate('/Cargo/CreateOrEdit', params);
    };

    const handleConfirmDialog = async () => {
        if (!selectedRow || excluindo.current || !permissoesCarregadas || !podeCancelar()) return;
        excluindo.current = true;
        try {
            const { data } = await CrudsAPI.cargo.deletar(selectedRow);
            setFlashMessage(data.message, 'success');
            setSelectedRow(null);
            if (dados.length === 1 && pagination.pageIndex > 0) {
                mrt.setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }));
            } else {
                await fetchData();
            }
        } catch (error) {
            setFlashMessage(error.response?.data?.message || 'Não foi possível excluir o cargo.', 'error');
        } finally {
            excluindo.current = false;
        }
    };

    const handleImprimir = async () => {
        if (!permissoesCarregadas || !podeImprimir()) return;
        let empresaIds;
        try {
            empresaIds = JSON.parse(localStorage.getItem('empresaIds'));
        } catch {
            empresaIds = null;
        }
        if (!Array.isArray(empresaIds) || !empresaIds.length) empresaIds = [empresaId];
        const params = new URLSearchParams({
            start: String(pagination.pageIndex * pagination.pageSize),
            size: String(pagination.pageSize),
            filters: JSON.stringify(columnFilters ?? []),
            filtersMode: JSON.stringify(columnFilterFns ?? {}),
            globalFilter: globalFilter ?? '',
            sorting: JSON.stringify(sorting ?? []),
            tipo: urlParams.get('tipo') ?? '',
            tabelas: 'rhCargo',
            empresaId: empresaIds,
        });
        try {
            const response = await CrudsAPI.cargo.imprimir(params, {});
            printPdf(response.data);
        } catch (error) {
            setFlashMessage(error.response?.data?.message || 'Não foi possível imprimir os cargos.', 'error');
        }
    };

    const columns = useMemo(
        () => [
            {
                id: 'Cargos',
                header: 'Cargos dos colaboradores utilizados no sistema',
                columns: [
                    {
                        accessorKey: 'Empresa.nomeFantasia',
                        header: 'Empresa',
                    },
                    {
                        accessorKey: 'codigo',
                        header: 'Código',
                    },
                    {
                        accessorKey: 'descricao',
                        header: 'Descrição',
                    },
                    {
                        accessorKey: 'sigla',
                        header: 'Sigla',
                    },
                    {
                        accessorKey: 'fator',
                        header: 'Fator',
                        muiTableBodyCellProps: {
                            align: 'right',
                        },
                        accessorFn: (row) =>
                            row.fator === null
                                ? ''
                                : parseFloat(row.fator)?.toLocaleString('pt-BR', {
                                      style: 'decimal',
                                      minimumFractionDigits: 8,
                                      maximumFractionDigits: 8,
                                      minimumIntegerDigits: 1,
                                      useGrouping: true,
                                  }),
                    },
                    {
                        accessorKey: 'rhCBO.codigo',
                        header: 'CBO',
                    },
                    {
                        accessorKey: 'rhCBO.descricao',
                        header: 'CBO descrição',
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        filterVariant: 'select', // Define o filtro como seletor
                        filterFn: 'equals', // Define o tipo de filtro como "igual"
                        filterSelectOptions: [
                            { label: 'Todos', value: '' }, // Opção padrão
                            { label: 'Sim', value: '1' }, // Ativo
                            { label: 'Não', value: '0' }, // Inativo
                        ],
                        muiTableBodyCellProps: {
                            align: 'center',
                        },
                        Cell: ({ row }) => {
                            const value = row.original.ativo;
                            return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                        },
                    },
                ],
            },
        ],
        []
    );

    const editarHabilitado = permissoesCarregadas && podeEditar();
    const cancelarHabilitado = permissoesCarregadas && podeCancelar();

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            {selectedRow && (
                <CustomDialog
                    title="Excluir cargo"
                    content="Você tem certeza?"
                    onClose={() => {
                        if (!excluindo.current) setSelectedRow(null);
                    }}
                    onConfirm={handleConfirmDialog}
                />
            )}
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    {...mrtDefault}
                    columns={columns}
                    data={dados}
                    getRowId={(row) => row.uuid}
                    muiTableContainerProps={{ sx: { maxWidth: '100%', overflowX: 'auto' } }}
                    renderRowActions={
                        isMobile
                            ? undefined
                            : ({ row }) => (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                      <Tooltip title="Editar">
                                          <span>
                                              <IconButton onClick={() => abrirCadastro(row)} disabled={!editarHabilitado}>
                                                  <Edit />
                                              </IconButton>
                                          </span>
                                      </Tooltip>
                                      <Tooltip title="Excluir">
                                          <span>
                                              <IconButton
                                                  color="error"
                                                  onClick={() => setSelectedRow(row)}
                                                  disabled={!cancelarHabilitado}
                                              >
                                                  <Delete />
                                              </IconButton>
                                          </span>
                                      </Tooltip>
                                  </Box>
                              )
                    }
                    renderRowActionMenuItems={
                        isMobile
                            ? ({ row, closeMenu }) => [
                                  <MenuItem
                                      key="editar"
                                      disabled={!editarHabilitado}
                                      onClick={() => {
                                          closeMenu();
                                          abrirCadastro(row);
                                      }}
                                  >
                                      <ListItemIcon>
                                          <Edit fontSize="small" />
                                      </ListItemIcon>
                                      Editar
                                  </MenuItem>,
                                  <MenuItem
                                      key="excluir"
                                      disabled={!cancelarHabilitado}
                                      onClick={() => {
                                          closeMenu();
                                          setSelectedRow(row);
                                      }}
                                  >
                                      <ListItemIcon>
                                          <Delete fontSize="small" color="error" />
                                      </ListItemIcon>
                                      Excluir
                                  </MenuItem>,
                              ]
                            : undefined
                    }
                    renderTopToolbarCustomActions={() => (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', py: 0.5 }}>
                            <Button
                                color="secondary"
                                variant="contained"
                                size={isMobile ? 'small' : 'medium'}
                                disabled={!permissoesCarregadas || !podeIncluir()}
                                onClick={() => abrirCadastro()}
                            >
                                Novo
                            </Button>
                            <Button
                                color="info"
                                variant="contained"
                                size={isMobile ? 'small' : 'medium'}
                                disabled={!permissoesCarregadas || !podeImprimir()}
                                onClick={handleImprimir}
                            >
                                Imprimir
                            </Button>
                        </Box>
                    )}
                />
            </ThemeProvider>
        </Box>
    );
};
export default Index;
